import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

import { Dialog } from "../../components/Dialog";
import { nl } from "../../i18n/nl";
import { DESKTOP_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { ReportFormFor } from "./ReportPage";

type OpenReport = (companyId?: string | null) => void;

const ReportContext = createContext<OpenReport | null>(null);

/** The company of the current page (`/bedrijven/{id}`), if any. */
export function companyIdFromPath(pathname: string): string | null {
  return /^\/bedrijven\/([^/]+)/.exec(pathname)?.[1] ?? null;
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** "Nieuwe melding": a dialog on desktop, the `/melden` screen on mobile; shortcut N. */
export function ReportProvider({ children }: { children: ReactNode }) {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const openReport = useCallback<OpenReport>(
    (id = null) => {
      if (desktop) {
        setCompanyId(id);
        setOpen(true);
      } else {
        void navigate(id ? `/melden?bedrijf=${encodeURIComponent(id)}` : "/melden");
      }
    },
    [desktop, navigate],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "n" || event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTyping(event.target) || document.querySelector('[role="dialog"]')) return;
      event.preventDefault();
      openReport(companyIdFromPath(pathname));
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openReport, pathname]);

  return (
    <ReportContext.Provider value={openReport}>
      {children}
      <Dialog open={open} onOpenChange={setOpen} title={nl.report.title} description={nl.report.lead}>
        {open && <ReportFormFor companyId={companyId} />}
      </Dialog>
    </ReportContext.Provider>
  );
}

export function useReport(): OpenReport {
  const open = useContext(ReportContext);
  if (!open) throw new Error("useReport must be used inside <ReportProvider>");
  return open;
}
